import { Box, Text } from "@chakra-ui/react";
import { Col, Row } from "antd";
import { ContaForm } from "features/conta/typings";
import {
  TipoDivisaoContaEnum,
  TipoRecorrenciaEnum,
  OperacaoEnum,
} from "core/resources/enums/conta";
import { Control, UseFormSetValue } from "react-hook-form";
import InputMoney from "components/Input/InputMoney";
import SelectDivisaoParcelas from "components/Input/SelectDivisaoParcelas";
import Field from "components/Input/Field";
import InputToggleContent from "components/Input/InputToggleContent";
import { useState, useEffect } from "react";
import { useDebounce } from "core/features/shared/useDebouce";
import colors from "core/resources/theme/colors";
import { roundTo } from "round-to";

interface RepetirProps {
  control: Control<ContaForm, object>;
  form: ContaForm;
  operacao: OperacaoEnum;
  setValue: UseFormSetValue<ContaForm>;
  title?: string;
  isUpdate?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
}

function Repetir({
  control,
  form,
  operacao,
  setValue,
  title = "Repetir",
  isUpdate = false,
  disabled = false,
  disabledMessage
}: RepetirProps) {
  const [numeroParcelas, setNumeroParcelas] = useState(form.numeroParcelas);
  const numeroParcelasDebounce = useDebounce(numeroParcelas, 200);

  const total = form.composicoes?.reduce(
    (acc, next) =>
      (acc += (next?.valor ?? 0) * (next?.operacao ?? 0) * operacao),
    0
  );

  const valorParcelas =
    form.parcelas?.reduce((acc, next) => (acc += next.valor), 0) || 0;

  const valorDiferente =
    form.tipoDivisaoContaId ===
    TipoDivisaoContaEnum.DividirValorLiquidoEntreParcelas &&
    roundTo(valorParcelas, 2) !== roundTo(total, 2);

  useEffect(() => {
    setValue("numeroParcelas", numeroParcelasDebounce);
  }, [numeroParcelasDebounce, setValue]);

  useEffect(() => {
    if (form.repetir) {
      setValue("tipoRecorrenciaId", TipoRecorrenciaEnum.Parcelada);
    } else {
      setValue("tipoRecorrenciaId", undefined);
    }
  }, [setValue, form.repetir]);

  return (
    <Box as={Row} mb={2}>
      <Box as={Col} sm={24}>
        <Field
          shouldUnregister
          control={control}
          name="repetir"
          render={({ field }) => (
            <>
              <InputToggleContent
                label={title}
                active={field.value}
                onToggle={field.onChange}
                disabled={disabled}
                disabledMessage={disabledMessage}
                size="sm"
              >
                <>
                  {/*<Field
                  control={control}
                  name="tipoRecorrenciaId"
                  shouldUnregister
                  rules={{
                    required: form.repetir,
                  }}
                  render={({ field, isInvalid }) => (
                     {/* <RadioGroup
                      w="fit-content"
                      display="flex"
                      flexDir="column"
                      value={field.value}
                      key={`${field.value}`}
                      onChange={field.onChange}
                      mb={8}
                    >
                     <Radio
                        isInvalid={isInvalid}
                        color={isInvalid ? "red" : undefined}
                        value={TipoRecorrenciaEnum.Fixa}
                        mb={4}
                      >
                        {operacao === OperacaoEnum.Despesa
                          ? "Despesa"
                          : "Receita"}{" "}
                        fixa mensal
                      </Radio> 
                      <Radio
                        isInvalid={isInvalid}
                        color={isInvalid ? "red" : undefined}
                        value={TipoRecorrenciaEnum.Parcelada}
                      >
                        {operacao === OperacaoEnum.Despesa
                          ? "Pagamento"
                          : "Recebimento"}{" "}
                        parcelado
                      </Radio>
                    </RadioGroup>
                  )}
                />*/}
                  {!disabled && form.tipoRecorrenciaId === TipoRecorrenciaEnum.Parcelada ? (
                    <>
                      <Box as={Row} gutter={10} mb={4}>
                        <Box as={Col} sm={19}>
                          <Field
                            shouldUnregister
                            control={control}
                            label="Valor das parcelas"
                            name="tipoDivisaoContaId"
                            rules={{
                              required: form.repetir,
                            }}
                            render={({ field, isInvalid }) => (
                              <SelectDivisaoParcelas
                                placeholder="Forma de divisão das parcelas"
                                value={field.value}
                                onChange={field.onChange}
                                isInvalid={isInvalid}
                                defaultValue={
                                  TipoDivisaoContaEnum.ParcelasTeraoMesmoValorLiquido
                                }
                              />
                            )}
                          />
                        </Box>

                        <Box as={Col} sm={5}>
                          <Field
                            shouldUnregister
                            control={control}
                            label="Parcelas"
                            name="numeroParcelas"
                            rules={{
                              required: form.repetir,
                            }}
                            render={({ field, isInvalid }) => (
                              <InputMoney
                                placeholder="0"
                                maxValue={360}
                                isInvalid={isInvalid}
                                onChange={(v: any) => setNumeroParcelas(v)}
                                value={numeroParcelas}
                                allowDecimals={false}
                                decimalScale={0}
                                decimalsLimit={0}
                              />
                            )}
                          />
                        </Box>
                      </Box>

                      {valorDiferente ? (
                        <Text color={colors.accentNegative} mb={4}>
                          O valor somado das parcelas deve ser igual ao valor
                          total da conta.
                        </Text>
                      ) : null}

                      {form.numeroParcelas && form.numeroParcelas > 0 ? (
                        <>
                          <Box as={Row} gutter={4} mb={2}>
                            <Box as={Col} sm={7}>
                              <Text>Vencimento</Text>
                            </Box>
                            <Box as={Col} sm={12}>
                              <Text>Resumo</Text>
                            </Box>
                            <Box as={Col} sm={5}>
                              <Text>Valor</Text>
                            </Box>
                          </Box>

                          {[...Array(form.numeroParcelas).keys()].map((_, i) => (
                            <Box as={Row} gutter={10} mb={2}>
                              <Box as={Col} sm={7}>
                                <Field
                                  shouldUnregister
                                  control={control}
                                  rules={{
                                    required: form.repetir,
                                  }}
                                  name={`parcelas.${i}.dataVencimento`}
                                  inputProps={{
                                    type: "date",
                                    disabled:
                                      !isUpdate &&
                                      i === 0 &&
                                      form.tipoDivisaoContaId ===
                                      TipoDivisaoContaEnum.ParcelasTeraoMesmoValorLiquido,
                                  }}
                                />
                              </Box>
                              <Box as={Col} sm={12}>
                                <Field
                                  shouldUnregister
                                  control={control}
                                  name={`parcelas.${i}.descricao`}
                                  rules={{
                                    required: form.repetir,
                                  }}
                                />
                              </Box>
                              <Box as={Col} sm={5}>
                                <Field
                                  shouldUnregister
                                  control={control}
                                  name={`parcelas.${i}.valor`}
                                  rules={{
                                    required: form.repetir,
                                  }}
                                  render={({ field, isInvalid }) => (
                                    <InputMoney
                                      isInvalid={isInvalid}
                                      onChange={field.onChange}
                                      value={field.value}
                                      minValue={0}
                                    />
                                  )}
                                />
                              </Box>
                            </Box>
                          ))}
                        </>
                      ) : null}
                    </>
                  ) : null}
                </>
              </InputToggleContent>
            </>
          )}
        />
      </Box>
    </Box>
  );
}

export default Repetir;
